<template>
  <TabbedBox :loading="pageDataLoading">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-layer-group"></i> Manual Usage - Glass Setup
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">
        <FormGroup :form="toolForm">
          <div class="row">
            <div class="col-12">
              <ReportFilterPopover class="float-right" headerTitle="Historical usage range">
                <template v-slot:popover>
                  <ReportDateRangeSelect 
                    :startDate="stockUsageStartDate"
                    :endDate="stockUsageEndDate"
                    @updateStartDate="stockUsageStartDate = $event"
                    @updateEndDate="stockUsageEndDate = $event"
                  />
                </template>
                <template v-slot:filterDisplay>
                  <div>
                    <i class="far fa-calendar mr-2"></i>{{stockUsageStartDate | formatDateCustom('MMM YYYY')}} - {{stockUsageEndDate | formatDateCustom('MMM YYYY')}}
                  </div>
                </template>
              </ReportFilterPopover>
            
              <ContentTitle>Glass Size Usage</ContentTitle>
              <ContentSubTitle>Historical usage stored as average usage of sheets per month</ContentSubTitle>
              <ContentSubTitle>Manual usage entry disabled for dates after the first stock transaction in the system</ContentSubTitle>

              <Spinner v-if="pageDataLoading" />

              <StaticDataTable class="static-table-list" :hover="true" :striped="false" smallRows v-show="!pageDataLoading">
                <template v-slot:header>
                  <tr>
                    <th>Stock</th>
                    <th>Size</th>
                    <template v-for="month of usageMonthList">
                      <th :key="month.index" width="6%">{{month.title}}</th>
                    </template>
                  </tr>
                </template>
                <template v-slot:body>
                  <template v-for="stock in stockList">
                    <tr :key="stock.id" class="static-datatable-striped">
                      <td>
                        <strong><LinkStock :name="`${stock.code} - ${stock.name}`" :id="stock.id" /></strong>
                      </td>
                      <td class="small">Size</td>
                      <template v-for="month of usageMonthList">
                        <td :key="month.index">{{month.title}}</td>
                      </template>
                    </tr>
                    <template v-for="(glassSize, index) in glassSizeListForStock(stock.id)">
                      <tr :key="`size-${index}-${glassSize.id}`">
                        <td class="font-weight-bold"><i class="fas fa-caret-right mr-1 ml-2"></i> Glass Size {{glassSize.height}}x{{glassSize.width}}</td>
                        <td>
                          {{glassSize.height}} x {{glassSize.width}}
                        </td>
                        <template v-for="month of glassSize.monthUsage">
                          <td :key="month.index">
                            <FormInputText v-model="month.averageUsage" :readonly="!month.enabled" />
                          </td>
                        </template>
                        
                      </tr>
                    </template>
                    <tr :key="`spacer-${stock.id}`">
                      <td :colspan="usageMonthList.length + 2" class="py-4">&nbsp;</td>
                    </tr>
                  </template>
                </template>
              </StaticDataTable>

              <FormHorizontalRule />
              <FormButton class="btn btn-primary btn-sm" type="submit" :loading="submitting" @click.prevent="submitGlassDetails">Update</FormButton>
            </div>
          </div>
        </FormGroup>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'

import form from '@/mixins/form'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'AdminToolGlassSetup',
  mixins: [form],
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  data() {
    return {
      stockList: [],
      glassSizeList: [],
      manualUsageInfo: {
        earliestStockTransaction: moment().local().subtract(12, 'months').startOf('month')
      },
      toolForm: {},
      submitting: false,

      stockUsageStartDate: moment().local().startOf('month').subtract(11, 'months').startOf('month').startOf('day').toDate().toISOString(),
      stockUsageEndDate: moment().local().subtract(1, 'month').endOf('month').toDate().toISOString(),
    }
  },
  watch: {
    stockUsageStartDate: function() {
      this.requestData()
    },
    stockUsageEndDate: function() {
      this.requestData()
    }
  },
  computed: {
    usageMonthList() {
      var stockUsageMonthCount = Math.abs(parseInt(Math.round(moment.duration(moment(this.stockUsageEndDate).diff(this.stockUsageStartDate)).asMonths())))
      stockUsageMonthCount = Math.min(stockUsageMonthCount, 36)

      var outputMonthList = []
      for(var monthIndex=0; monthIndex<stockUsageMonthCount; monthIndex++) {
        let monthStart = moment(this.stockUsageStartDate).local().startOf('month').add(monthIndex, 'months')
        let monthName = monthStart.format('MMM \'YY')

        outputMonthList.push({ title: monthName, index: monthIndex, monthStart: monthStart, monthEnd: monthStart.clone().endOf('month') })
      }

      return outputMonthList
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      let usageParams = {
        usageStartDate: this.stockUsageStartDate,
        usageEndDate: this.stockUsageEndDate
      }

      this.pageLoadData(Promise.all([
        this.$apiService.inventoryService.getStockFilteredList('', InventoryStockTypes.glass),
        this.$apiService.adminService.getManualUsageGlassSizes(usageParams),
        this.$apiService.adminService.getManualUsageInfo()
      ]))
      .then(results => {
        this.manualUsageInfo = results[2].data

        // Filter out stock with no glass sizes
        this.stockList = results[0].data.filter(stock => {
          if(results[1].data.find(glassSize => glassSize.stockId == stock.id) == null)
            return false
          return true
        })
        this.glassSizeList = results[1].data.map(glassSize => {
          var output = _.cloneDeep(glassSize)

          // Split up the usage data into the months
          output.monthUsage = this.usageMonthList.map(monthInfo => {
            // Note: Only using 'production' usage
            // Not breaking down into 2 usage fields in this tool
            var usageInfo = {
              logDate: monthInfo.monthStart.toDate().toISOString(),
              averageUsage: 0,
              enabled: true
            }

            let monthLogList = glassSize.stockUsageHistoryLogs.filter(usageLog => {
              return moment(usageLog.logDate).isBetween(monthInfo.monthStart, monthInfo.monthEnd, null, '[]')
            })

            // If the date is after the earliest stock transaction, disable entry for the log
            if(moment(usageInfo.logDate).isSameOrAfter(this.manualUsageInfo.earliestStockTransaction))
              usageInfo.enabled = false

            usageInfo.averageUsage += monthLogList.reduce((curValue, log) => curValue + log.sheetProductionUsage, 0)
            usageInfo.averageUsage += monthLogList.reduce((curValue, log) => curValue + log.sheetStockUsage, 0)

            return usageInfo
          })
          return output
        })
        
      })
    },
    glassSizeListForStock(stockId) {
      return this.glassSizeList.filter(glassSize => glassSize.stockId == stockId)
    },
    submitGlassDetails() {
      this.submitting = true

      var usageData = {
        glassSizeUsageData: this.convertGlassSizeUsageList()
      }
     
      this.$data.toolForm = this.clearFormResponse()

      this.$apiService.adminService.updateManualUsageGlassSizes(usageData)
      .then(response => {
        this.submitting = false

        this.successToast('Glass Manual Usage Updated')
        this.$router.push(`/admin/tools`)
      })
      .catch(err => {
        // Process errors
        this.$data.toolForm = this.processFormResponse(err)
        this.submitting = false
      })
    },

    // Prepare the glass size list for updating usage data
    convertGlassSizeUsageList() {
      return this.glassSizeList.map(glassSize => {
        var output = {
          glassSizeId: glassSize.id,
          stockId: glassSize.stockId,
          stockUsageHistoryLogs: []
        }

        output.stockUsageHistoryLogs = glassSize.monthUsage.map(monthInfo => {
          return {
            logDate: monthInfo.logDate,
            sheetProductionUsage: monthInfo.averageUsage,
            sheetStockUsage: 0
          }
        })

        return output
      })
    }
  }
}
</script>